import { CANVAS_SIZE } from '@/constant';
import { useZoom } from '@/hooks/useZoom';
import { SlideState } from '@/pages/LessonEditor/types';
import { PropsWithPreview, Question, SlideComponent as SlideComponentModel } from '@/types/slide';
import { Paper } from '@mui/material';
import React, { CSSProperties, useImperativeHandle } from 'react';
import { SlideComponent } from './SlideComponent';

export interface SlideRef {
    size: () => typeof CANVAS_SIZE;
    scale: () => number;
    containerRef: React.RefObject<HTMLDivElement | null>;
}

type SlideProp = {
    components: SlideComponentModel[];
    state?: SlideState;
    background?: CSSProperties['backgroundColor'];
    /**
     * Question and associated answers, we are only using the answer of it
     * so we can show the result of the question in a holistic way.
     */
    questions?: Question[];
};

export const Slide: React.FC<PropsWithPreview<SlideProp>> = ({ components, state, background, preview, questions, interactive }) => {
    const selfRef = React.useRef<SlideRef>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const scale = useZoom(containerRef, undefined);

    useImperativeHandle(selfRef, () => {
        return {
            size() {
                return CANVAS_SIZE;
            },
            scale() {
                return scale;
            },
            containerRef: containerRef,
        };
    }, [scale]);

    return (
        <Paper aria-label='slide' sx={{ backgroundColor: background ? background : 'white', height: '100%', width: '100%', borderRadius: 0, color: 'black' }} ref={containerRef}>
            <div style={{ height: '100%', width: '100%', scale: `${scale}`, transformOrigin: '0 0' }} ref={containerRef}>
                {components.map((component, index) => {
                    return (
                        <SlideComponent
                            key={`slide-component-${index}`}
                            component={component}
                            init={{ focus: state?.focus?.id === component.id }}
                            preview={preview}
                            question={questions?.find((q) => q.id === component.id)}
                            slideRef={selfRef}
                            interactive={interactive}
                        />
                    );
                })}
            </div>
        </Paper>
    );
};
